import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { toast } from "sonner";
import {
  Shield, Save, Loader2, UserCog, RotateCcw, Lock, History, X,
  CheckCircle2, XCircle, Eye, EyeOff, Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue }
  from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription }
  from "@/components/ui/sheet";
import PageHeader from "@/components/PageHeader";

/* -------------------------------------------------------------------- */
const titleCase = (s) => (s || "").replace(/_/g, " ").replace(/\w\S*/g,
  (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());

const FIELD_LVL = [
  { v: "visible",   label: "Visible",  icon: Eye,     cls: "text-[#39FF14]" },
  { v: "view_only", label: "View Only",icon: Pencil,  cls: "text-amber-300" },
  { v: "hidden",    label: "Hidden",   icon: EyeOff,  cls: "text-red-300"   },
];

function FieldLevelPill({ level }) {
  const l = FIELD_LVL.find((x) => x.v === level) || FIELD_LVL[0];
  const Icon = l.icon;
  return (
    <span className={`text-xs flex items-center gap-1 ${l.cls}`}>
      <Icon size={10} /> {l.label}
    </span>
  );
}

/* -------------------------------------------------------------------- */
/*                           Role tab                                   */
/* -------------------------------------------------------------------- */
function RoleMatrixEditor({ registry }) {
  const [role, setRole] = useState("");
  const [perms, setPerms] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const roles = registry?.roles || [];
  useEffect(() => {
    if (!role && roles.length) {
      const first = roles.find((r) => r !== "admin");
      if (first) setRole(first);
    }
  }, [role, roles]);

  const load = useCallback(async () => {
    if (!role) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/access-control/role/${role}`);
      setPerms(data?.permissions || {});
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [role]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/access-control/role/${role}`, { permissions: perms });
      toast.success(`Permissions saved for ${titleCase(role)}`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    setSaving(true);
    try {
      // PUT with empty permissions re-seeds from default matrix
      await api.put(`/admin/access-control/role/${role}`, { permissions: {} });
      await load();
      toast.success(`Reset ${titleCase(role)} to defaults`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleAction = (mod, action, val) => {
    setPerms((p) => ({
      ...p,
      [mod]: { ...p[mod], actions: { ...p[mod].actions, [action]: val } },
    }));
  };
  const toggleModule = (mod, val) => {
    setPerms((p) => ({ ...p, [mod]: { ...p[mod], module_access: val } }));
  };
  const setField = (mod, field, lvl) => {
    setPerms((p) => ({
      ...p,
      [mod]: { ...p[mod], fields: { ...p[mod].fields, [field]: lvl } },
    }));
  };

  const isAdminRole = role === "admin";
  const floor = registry?.hard_coded_floor || {};
  const enforced = new Set(registry?.field_redaction_enforced_modules || []);

  return (
    <div className="space-y-4" data-testid="ac-role-editor">
      <div className="flex items-center gap-3">
        <label className="text-xs text-[#8A8F98] uppercase tracking-wider">Role</label>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-[260px] bg-[#121815] border-[#3A4640]"
                         data-testid="ac-role-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#121815] border-[#3A4640] max-h-[320px]">
            {roles.map((r) => (
              <SelectItem key={r} value={r}>{titleCase(r)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={resetDefaults} disabled={isAdminRole || saving}
                  data-testid="ac-role-reset-btn">
            <RotateCcw size={14} className="mr-1" /> Reset
          </Button>
          <Button onClick={save} disabled={isAdminRole || saving}
                  className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90"
                  data-testid="ac-role-save-btn">
            {saving ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <Save size={14} className="mr-1.5"/>}
            Save
          </Button>
        </div>
      </div>

      {isAdminRole && (
        <div className="p-3 rounded-[10px] bg-[#2d4a5e]/30 border border-sky-500/40 text-sm">
          <Shield size={14} className="inline mr-1 text-sky-200" />
          Admin role is immutable (full access is required for system
          operation). Hard-coded security floors cannot be overridden.
        </div>
      )}

      {loading && (
        <div className="py-10 flex items-center justify-center text-[#8A8F98]">
          <Loader2 className="animate-spin" />
        </div>
      )}

      {perms && (
        <div className="space-y-3">
          {Object.entries(registry.modules).map(([mod, meta]) => {
            const row = perms[mod] || { module_access: false, actions: {}, fields: {} };
            const floored = (floor[mod] || []).includes(role);
            const fields = registry.sensitive_fields?.[mod] || [];
            return (
              <section key={mod}
                       className={`p-3 rounded-[10px] border ${
                         floored ? "border-red-500/40 bg-red-500/5"
                                 : "border-[#3A4640] bg-[#121815]"}`}
                       data-testid={`ac-module-${mod}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{titleCase(mod)}</span>
                    {floored && (
                      <Badge variant="outline" className="bg-red-500/10 text-red-300 border-red-500/40">
                        <Lock size={10} className="mr-1" /> Hard-coded Denied
                      </Badge>
                    )}
                    {enforced.has(mod) && (
                      <Badge variant="outline" className="bg-[#1a3524] text-[#39FF14] border-[#39FF14]/40">
                        Field enforcement
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#8A8F98] uppercase tracking-wider">Module Access</span>
                    <Switch
                      checked={!!row.module_access && !floored}
                      disabled={isAdminRole || floored}
                      onCheckedChange={(v) => toggleModule(mod, v)}
                      data-testid={`ac-mod-access-${mod}`}
                    />
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {meta.actions.map((a) => (
                    <div key={a} className={`flex items-center justify-between px-3 py-2 rounded-md border ${
                      floored ? "border-red-500/30 opacity-50"
                              : "border-[#3A4640] bg-[#0B0F0C]"
                    }`}>
                      <span className="text-sm">{titleCase(a)}</span>
                      <Switch
                        checked={!!row.actions?.[a] && !floored}
                        disabled={isAdminRole || floored}
                        onCheckedChange={(v) => toggleAction(mod, a, v)}
                        data-testid={`ac-act-${mod}-${a}`}
                      />
                    </div>
                  ))}
                </div>

                {fields.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#3A4640]">
                    <div className="text-xs text-[#8A8F98] uppercase tracking-wider mb-2">
                      Sensitive Fields {!enforced.has(mod) && (
                        <span className="normal-case text-[#8A8F98]/70">
                          — registered, enforcement wired V2
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {fields.map((f) => (
                        <div key={f} className="flex items-center justify-between px-3 py-2 rounded-md border border-[#3A4640] bg-[#0B0F0C]">
                          <code className="text-xs text-[#8A8F98]">{f}</code>
                          <Select
                            value={row.fields?.[f] || "visible"}
                            onValueChange={(v) => setField(mod, f, v)}
                            disabled={isAdminRole || floored}
                          >
                            <SelectTrigger className="w-[130px] h-8 bg-[#121815] border-[#3A4640] text-xs"
                              data-testid={`ac-fld-${mod}-${f}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121815] border-[#3A4640]">
                              {FIELD_LVL.map((l) => (
                                <SelectItem key={l.v} value={l.v}>{l.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*                       User Overrides tab                             */
/* -------------------------------------------------------------------- */
function UserOverrides({ registry }) {
  const [users, setUsers] = useState([]);
  const [uid, setUid] = useState("");
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users");
        const list = Array.isArray(data) ? data : (data.users || data.items || []);
        setUsers(list.filter((u) => (u.role || "").toLowerCase() !== "admin"));
      } catch { setUsers([]); }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!uid) { setState(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/access-control/user/${uid}`);
      setState(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [uid]);
  useEffect(() => { load(); }, [load]);

  const setAction = (mod, action, value) => {
    // tri-state: true = allow, false = deny, null = inherit
    setState((s) => {
      const ov = { ...(s.overrides || {}) };
      ov[mod] = { ...(ov[mod] || {}),
                  actions: { ...(ov[mod]?.actions || {}), [action]: value } };
      return { ...s, overrides: ov };
    });
  };
  const setFieldOv = (mod, field, lvl) => {
    setState((s) => {
      const ov = { ...(s.overrides || {}) };
      ov[mod] = { ...(ov[mod] || {}),
                  fields: { ...(ov[mod]?.fields || {}), [field]: lvl } };
      return { ...s, overrides: ov };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/access-control/user/${uid}`, { overrides: state.overrides });
      await load();
      toast.success("Overrides saved");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const clearModule = async (mod) => {
    setSaving(true);
    try {
      await api.delete(`/admin/access-control/user/${uid}/module/${mod}`);
      await load();
      toast.success(`Overrides cleared for ${titleCase(mod)}`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const floor = registry?.hard_coded_floor || {};

  return (
    <div className="space-y-4" data-testid="ac-user-editor">
      <div className="flex items-center gap-3">
        <label className="text-xs text-[#8A8F98] uppercase tracking-wider">User</label>
        <Select value={uid} onValueChange={setUid}>
          <SelectTrigger className="w-[320px] bg-[#121815] border-[#3A4640]"
                         data-testid="ac-user-select"><SelectValue placeholder="Select a user…" /></SelectTrigger>
          <SelectContent className="bg-[#121815] border-[#3A4640] max-h-[320px]">
            {users.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.name || u.email}
                <span className="text-xs text-[#8A8F98] ml-2">· {titleCase(u.role)}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {uid && (
          <Button onClick={save} disabled={saving}
                  className="ml-auto bg-[#39FF14] text-[#0B0F0C] hover:bg-[#39FF14]/90"
                  data-testid="ac-user-save-btn">
            {saving ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <Save size={14} className="mr-1.5"/>}
            Save
          </Button>
        )}
      </div>

      {loading && (
        <div className="py-10 flex items-center justify-center text-[#8A8F98]">
          <Loader2 className="animate-spin" />
        </div>
      )}

      {state && (
        <>
          <div className="p-3 rounded-[10px] bg-[#121815] border border-[#3A4640]">
            <div className="text-sm">
              <UserCog size={14} className="inline mr-2 text-[#39FF14]" />
              <span className="font-medium">{state.user?.name || state.user?.email}</span>
              <span className="text-xs text-[#8A8F98] ml-2">· Inherits from role: {titleCase(state.user?.role)}</span>
            </div>
            <div className="text-xs text-[#8A8F98] mt-2">
              Overrides below take precedence over the role's inherited permissions. Hard-coded security
              floors cannot be overridden by any user override.
            </div>
          </div>

          {Object.entries(registry.modules).map(([mod, meta]) => {
            const inherited = state.inherited?.[mod] || {};
            const eff = state.effective?.[mod] || {};
            const ov = state.overrides?.[mod] || {};
            const floored = (floor[mod] || []).includes(state.user?.role);
            const fields = registry.sensitive_fields?.[mod] || [];
            return (
              <section key={mod} className="p-3 rounded-[10px] bg-[#121815] border border-[#3A4640]"
                       data-testid={`ac-u-mod-${mod}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{titleCase(mod)}</span>
                    {floored && (
                      <Badge variant="outline" className="bg-red-500/10 text-red-300 border-red-500/40">
                        <Lock size={10} className="mr-1" /> Hard-coded Denied (cannot override)
                      </Badge>
                    )}
                    <Badge variant="outline"
                           className={eff.module_access
                             ? "bg-[#1a3524] text-[#39FF14] border-[#39FF14]/40"
                             : "bg-[#2d2d2d] text-slate-400 border-slate-500/30"}>
                      Effective: {eff.module_access ? "ON" : "OFF"}
                    </Badge>
                  </div>
                  {!!Object.keys(ov).length && (
                    <Button variant="ghost" size="sm" onClick={() => clearModule(mod)} data-testid={`ac-u-clear-${mod}`}>
                      <X size={12} className="mr-1" /> Clear overrides
                    </Button>
                  )}
                </div>

                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {meta.actions.map((a) => {
                    const inheritedVal = !!inherited.actions?.[a];
                    const effVal = !!eff.actions?.[a];
                    const ovVal = ov.actions?.[a];
                    return (
                      <div key={a} className="px-3 py-2 rounded-md border border-[#3A4640] bg-[#0B0F0C]">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">{titleCase(a)}</span>
                          <span className="text-xs text-[#8A8F98]">
                            {effVal ? <CheckCircle2 size={12} className="text-[#39FF14] inline"/> : <XCircle size={12} className="text-red-300 inline"/>}
                          </span>
                        </div>
                        <div className="mt-2 flex items-center gap-1">
                          {[["inherit", null, "Inherit"], ["allow", true, "Allow"], ["deny", false, "Deny"]].map(([k, v, lbl]) => (
                            <button key={k}
                                    disabled={floored}
                                    onClick={() => setAction(mod, a, v)}
                                    className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                      ovVal === v
                                        ? (v === true  ? "bg-[#1a3524] text-[#39FF14] border-[#39FF14]/40" :
                                           v === false ? "bg-red-500/10 text-red-300 border-red-500/40" :
                                                         "bg-[#2d4a5e]/30 text-sky-200 border-sky-500/40")
                                        : "border-[#3A4640] text-[#8A8F98] hover:text-[#F8F9FA]"
                                    }`}
                                    data-testid={`ac-u-${mod}-${a}-${k}`}
                            >
                              {lbl}
                            </button>
                          ))}
                        </div>
                        <div className="text-[10px] text-[#8A8F98]/80 mt-1">
                          inherited: {inheritedVal ? "Allow" : "Deny"}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {fields.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#3A4640]">
                    <div className="text-xs text-[#8A8F98] uppercase tracking-wider mb-2">Sensitive Fields</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {fields.map((f) => (
                        <div key={f} className="flex items-center justify-between px-3 py-2 rounded-md border border-[#3A4640] bg-[#0B0F0C]">
                          <div>
                            <code className="text-xs text-[#F8F9FA]">{f}</code>
                            <div className="text-[10px] text-[#8A8F98] mt-1">
                              Effective: <FieldLevelPill level={eff.fields?.[f] || "visible"} />
                            </div>
                          </div>
                          <Select
                            value={ov.fields?.[f] ?? "__inherit__"}
                            onValueChange={(v) => setFieldOv(mod, f, v === "__inherit__" ? null : v)}
                            disabled={floored}
                          >
                            <SelectTrigger className="w-[140px] h-8 bg-[#121815] border-[#3A4640] text-xs"
                              data-testid={`ac-u-fld-${mod}-${f}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121815] border-[#3A4640]">
                              <SelectItem value="__inherit__">Inherit</SelectItem>
                              {FIELD_LVL.map((l) => (
                                <SelectItem key={l.v} value={l.v}>{l.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*                          Audit Drawer                                */
/* -------------------------------------------------------------------- */
function AuditDrawer({ open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/access-control/audit", { params: { limit: 200 } });
        setRows(data?.items || []);
      } catch { setRows([]); }
      finally { setLoading(false); }
    })();
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="bg-[#0B0F0C] border-l border-[#3A4640] text-[#F8F9FA] w-full sm:max-w-[600px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Access Control Audit Log</SheetTitle>
          <SheetDescription className="text-[#8A8F98]">
            Every permission change is recorded here. Retention: forever.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-2" data-testid="ac-audit-list">
          {loading && <Loader2 className="animate-spin" />}
          {!loading && !rows.length && <div className="text-sm text-[#8A8F98]">No audit entries yet.</div>}
          {rows.map((r) => (
            <div key={r.id} className="p-3 rounded-[10px] bg-[#121815] border border-[#3A4640] text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium">
                  {titleCase(r.target_kind)}: {r.target_id}
                </span>
                <span className="text-xs text-[#8A8F98]">{formatDate(r.at)}</span>
              </div>
              <div className="text-xs text-[#8A8F98] mt-1">
                by {r.actor_name} ({titleCase(r.actor_role)})
              </div>
              {r.module && <div className="text-xs mt-1">Module: {titleCase(r.module)}</div>}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* -------------------------------------------------------------------- */
/*                              Main                                    */
/* -------------------------------------------------------------------- */
export default function AccessControl() {
  const [tab, setTab] = useState("roles");
  const [registry, setRegistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auditOpen, setAuditOpen] = useState(false);
  const [notAdmin, setNotAdmin] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/access-control/registry");
        setRegistry(data);
      } catch (e) {
        if (e?.response?.status === 403) setNotAdmin(true);
        else toast.error(formatApiError(e?.response?.data?.detail));
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-6 text-[#8A8F98]"><Loader2 className="inline animate-spin"/> Loading…</div>;
  if (notAdmin) return (
    <div className="p-6">
      <div className="p-4 rounded-[10px] bg-red-500/10 border border-red-500/40 text-red-200">
        <Lock size={14} className="inline mr-2"/>
        Access Control is restricted to Admin accounts.
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="access-control-page">
      <PageHeader
        title="Access Control"
        subtitle="Centralized role, user, and field permissions. Hard-coded security floors remain in force."
        action={
          <Button variant="ghost" onClick={() => setAuditOpen(true)} data-testid="ac-audit-btn">
            <History size={14} className="mr-1.5" /> Audit Log
          </Button>
        }
      />
      <div className="flex items-center gap-1 border-b border-[#3A4640]">
        {[["roles","Roles"],["users","User Overrides"]].map(([k, lbl]) => (
          <button key={k}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === k
                ? "border-[#39FF14] text-[#39FF14]"
                : "border-transparent text-[#8A8F98] hover:text-[#F8F9FA]"
            }`}
            data-testid={`ac-tab-${k}`}
          >
            {lbl}
          </button>
        ))}
      </div>
      {tab === "roles" && <RoleMatrixEditor registry={registry} />}
      {tab === "users" && <UserOverrides registry={registry} />}
      <AuditDrawer open={auditOpen} onClose={() => setAuditOpen(false)} />
    </div>
  );
}
